import { Box, Button, TextField } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getSubscriptionStatus } from 'Actions/app';
import { useAuthWrapperContext } from 'Contexts/AuthWrapperContext';
import { loadStripe } from '@stripe/stripe-js/pure';
import {
  Elements,
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from '@stripe/react-stripe-js';
import axios from 'axios';
import { MainAppReducer } from 'Types';
import { PLAN_IDS, PLAN_DETAILS, formatPrice, type PlanId } from 'Billing/Catalog';
import { usePricingCatalog } from 'Billing/usePricingCatalog';
import './Settings.css';

const stripePromise = process.env.REACT_APP_STRIPE_PK
  ? loadStripe(process.env.REACT_APP_STRIPE_PK)
  : null;
const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v1/`;
interface BillingDetails {
  hasPaymentMethodId: boolean;
  isCustomer: boolean;
  hasRecoverableSubscription?: boolean;
  canChangePlan?: boolean;
  subscriptionPlanAmount: number;
  subscriptionPlanInterval: string;
  subscriptionPlanIntervalCount?: number;
  subscriptionPlanCurrency?: string;
  trialDaysRemaining: number;
  hasCoupon: boolean;
  pendingPlan?: {
    amount: number;
    currency: string;
    interval: string;
    intervalCount: number;
    effectiveAt?: number | string;
  } | null;
  planChangeEffectiveAt?: number | string | null;
  outstandingInvoice?: {
    amountDue: number;
    currency: string;
    hostedInvoiceUrl?: string | null;
  } | null;
}
interface PaymentResponse {
  error?: string;
  status?: string;
  clientSecret?: string;
  intentType?: 'setup' | 'payment';
}
const errorMessage = (error: unknown) =>
  (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
  (error as Error)?.message ||
  'Unable to update your billing. Please try again.';

function currentPlanDescription(details: BillingDetails) {
  const quarterly = details.subscriptionPlanInterval === 'threeMonth';
  const count = details.subscriptionPlanIntervalCount ?? (quarterly ? 3 : 1);
  const interval = quarterly ? 'month' : details.subscriptionPlanInterval;
  if (!['month', 'year'].includes(interval) || !Number.isFinite(details.subscriptionPlanAmount))
    return 'Your current subscription';
  const frequency = count === 1 ? interval : `${count} ${interval}s`;
  return `${formatPrice(details.subscriptionPlanAmount, details.subscriptionPlanCurrency || 'usd')} per ${frequency}`;
}

function PaymentMethodForm({ onSaved }: { onSaved: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const { user, currentAuthToken } = useAuthWrapperContext();
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const busy = useRef(false);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy.current || !stripe || !elements) return;
    const card = elements.getElement(CardNumberElement);
    if (!card) return;
    busy.current = true;
    setLoading(true);
    setError(undefined);
    try {
      const result = await stripe.createPaymentMethod({
        type: 'card',
        card,
        billing_details: { name, email: user?.attributes?.email },
      });
      if (result.error || !result.paymentMethod)
        throw new Error(result.error?.message || 'Please check your card details.');
      const token = await currentAuthToken();
      // Changing a payment method must never silently change an existing price or coupon.
      const update = async (setupIntentId?: string) => {
        const { data } = await axios.patch<PaymentResponse>(
          `${baseUrl}stripe/update-plan`,
          {
            paymentMethodId: result.paymentMethod!.id,
            ...(setupIntentId ? { setupIntentId } : {}),
          },
          { headers: { Authorization: `Bearer ${token}` }, timeout: 20000 },
        );
        if (data.error) throw new Error(data.error);
        return data;
      };
      let data = await update();
      if (data.status === 'requires_confirmation' && data.intentType === 'setup') {
        if (!data.clientSecret) throw new Error('Card confirmation is unavailable. Please retry.');
        const confirmation = await stripe.confirmCardSetup(data.clientSecret);
        if (confirmation.error || confirmation.setupIntent?.status !== 'succeeded')
          throw new Error(confirmation.error?.message || 'Card confirmation is still pending.');
        // Resume the server operation after SCA, including paused/unpaid recovery.
        // The server verifies ownership and status of this SetupIntent before use.
        data = await update(confirmation.setupIntent.id);
      }
      if (data.status === 'requires_confirmation' && data.intentType === 'payment') {
        if (!data.clientSecret)
          throw new Error('Payment confirmation is unavailable. Please retry.');
        const confirmation = await stripe.confirmCardPayment(data.clientSecret);
        if (confirmation.error || confirmation.paymentIntent?.status !== 'succeeded')
          throw new Error(confirmation.error?.message || 'Payment confirmation is still pending.');
      } else if (data.status !== 'success') {
        throw new Error('Billing recovery is incomplete. Please retry.');
      }
      onSaved();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      busy.current = false;
      setLoading(false);
    }
  };
  const options = {
    style: { base: { color: '#CED0D4', fontSize: '16px', '::placeholder': { color: '#ADB1B7' } } },
  };
  return (
    <Box component="form" onSubmit={submit} sx={{ display: 'grid', gap: 2, maxWidth: 600, mt: 2 }}>
      <TextField
        className="name-input"
        placeholder="Cardholder name"
        value={name}
        onChange={(event) => setName(event.target.value)}
      />
      <Box className="card-container">
        <CardNumberElement options={options} />
      </Box>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Box className="card-container" sx={{ flex: 1 }}>
          <CardExpiryElement options={options} />
        </Box>
        <Box className="card-container" sx={{ flex: 1 }}>
          <CardCvcElement options={options} />
        </Box>
      </Box>
      {error && <p role="alert">{error}</p>}
      <Button type="submit" variant="contained" disabled={loading || !stripe}>
        {loading ? 'Saving…' : 'Save payment details'}
      </Button>
      <p>
        Updating your card keeps your current plan and discounts. If you have an unpaid invoice,
        your card may be charged to settle it at its original price.
      </p>
    </Box>
  );
}

const Billing = () => {
  const dispatch = useDispatch();
  const { userData } = useSelector((state: MainAppReducer) => state.mainAppReducer);
  const { currentAuthToken } = useAuthWrapperContext();
  const storeManaged =
    userData?.subscription_provider === 'apple' || userData?.subscription_provider === 'google';
  const pricing = usePricingCatalog(!storeManaged);
  const [details, setDetails] = useState<BillingDetails | null>(null);
  const [loading, setLoading] = useState(!storeManaged);
  const [error, setError] = useState<string>();
  const [message, setMessage] = useState<string>();
  const [selected, setSelected] = useState<PlanId | null>(null);
  const [updating, setUpdating] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [revision, setRevision] = useState(0);
  const busy = useRef(false);
  const canChangePlan =
    details?.canChangePlan ?? ['active', 'trialing'].includes(userData?.subscription_status || '');

  useEffect(() => {
    if (storeManaged) return;
    let canceled = false;
    setLoading(true);
    setError(undefined);
    (async () => {
      try {
        const token = await currentAuthToken();
        const { data } = await axios.get<{ details: BillingDetails }>(
          `${baseUrl}stripe/user-billing-details`,
          { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 },
        );
        if (!canceled) setDetails(data.details);
      } catch (cause) {
        if (!canceled) setError(errorMessage(cause));
      } finally {
        if (!canceled) setLoading(false);
      }
    })();
    return () => {
      canceled = true;
    };
  }, [storeManaged, currentAuthToken, revision]);

  const changePlan = async () => {
    if (!selected || busy.current || !pricing.catalog || !canChangePlan) return;
    busy.current = true;
    setUpdating(true);
    setError(undefined);
    setMessage(undefined);
    try {
      const token = await currentAuthToken();
      const { data } = await axios.patch<PaymentResponse>(
        `${baseUrl}stripe/update-plan`,
        { plan: selected },
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 20000,
        },
      );
      if (data.error) throw new Error(data.error);
      if (data.status !== 'success')
        throw new Error(
          'Your plan change requires billing recovery. Please update your payment details first.',
        );
      setMessage(
        userData?.subscription_status === 'trialing'
          ? 'Your plan for after the trial has been updated. Your trial end date is unchanged.'
          : 'Your plan change is scheduled for your next renewal. Your current price remains in place until then.',
      );
      setSelected(null);
      setRevision((value) => value + 1);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      busy.current = false;
      setUpdating(false);
    }
  };

  const cancelScheduledChange = async () => {
    if (busy.current) return;
    busy.current = true;
    setUpdating(true);
    setError(undefined);
    try {
      const token = await currentAuthToken();
      await axios.patch(
        `${baseUrl}stripe/cancel-plan-change`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 20000,
        },
      );
      setMessage(
        'Your scheduled change was canceled. Your current plan and renewal price are unchanged.',
      );
      setSelected(null);
      setRevision((value) => value + 1);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      busy.current = false;
      setUpdating(false);
    }
  };

  return (
    <Box
      sx={{
        height: 'calc(100dvh - 200px)',
        overflowY: 'auto',
        p: 3,
        color: '#CED0D4',
        fontFamily: 'Lato',
        maxWidth: 760,
        '& .MuiButton-root, & a': { color: '#9bc8ff' },
        '& .MuiButton-contained': { backgroundColor: '#ca7c62', color: '#071423' },
        '& .MuiButton-root.Mui-disabled': {
          color: '#8993a1',
          backgroundColor: 'rgba(255,255,255,0.08)',
        },
      }}
    >
      {storeManaged ? (
        <>
          <h2>Subscription</h2>
          <p>
            Your subscription is managed by{' '}
            {userData?.subscription_provider === 'apple' ? 'Apple' : 'Google Play'}. Your existing
            renewal terms remain with that store.
          </p>
          <p>
            {userData?.subscription_provider === 'apple'
              ? 'Manage or cancel it in Settings → Apple ID → Subscriptions.'
              : 'Manage or cancel it in Google Play → Payments & subscriptions → Subscriptions.'}
          </p>
        </>
      ) : (
        <>
          <h2>Current plan</h2>
          {loading && <p role="status">Loading billing details…</p>}
          {!loading &&
            details &&
            (!details.isCustomer || details.hasRecoverableSubscription === false) && (
              <p>
                No current subscription to manage. Choose a new plan from the subscription prompt,
                or contact support@museflow.ai if your account still shows an active subscription.
              </p>
            )}
          {!loading && details?.isCustomer && details.hasRecoverableSubscription !== false && (
            <>
              {userData?.subscription_status === 'trialing' && (
                <p>Free trial: {details.trialDaysRemaining} days remaining.</p>
              )}
              <p>
                {currentPlanDescription(details)}
                {userData?.subscription_status === 'trialing' ? ' after your trial' : ''}
              </p>
              {details.hasCoupon && <p>Your existing subscription discount is preserved.</p>}
              {details.pendingPlan && (
                <p>
                  A plan change to{' '}
                  {formatPrice(details.pendingPlan.amount, details.pendingPlan.currency)} per{' '}
                  {details.pendingPlan.intervalCount === 1
                    ? details.pendingPlan.interval
                    : `${details.pendingPlan.intervalCount} ${details.pendingPlan.interval}s`}{' '}
                  is scheduled for your next renewal.
                </p>
              )}
              {details.pendingPlan && (
                <Button onClick={cancelScheduledChange} disabled={updating}>
                  Cancel scheduled change
                </Button>
              )}
              {canChangePlan && (
                <>
                  <h2>Change plan</h2>
                  <p>
                    {userData?.subscription_status === 'trialing'
                      ? 'Choose your plan for after the trial. Your trial end date stays the same.'
                      : 'Changes take effect at your next renewal. Until then, your current price and access continue.'}
                  </p>
                  {pricing.loading && <p>Loading available plans…</p>}
                  {pricing.catalog && (
                    <Box sx={{ display: 'grid', gap: 1 }}>
                      {PLAN_IDS.map((id) => {
                        const plan = pricing.catalog!.plans.find((item) => item.id === id)!;
                        return (
                          <Button
                            key={id}
                            variant={selected === id ? 'contained' : 'outlined'}
                            aria-pressed={selected === id}
                            onClick={() => setSelected(id)}
                            disabled={updating}
                          >
                            {PLAN_DETAILS[id].title}:{' '}
                            {formatPrice(plan.amountCents / 100, pricing.catalog!.currency)} per{' '}
                            {PLAN_DETAILS[id].frequency}
                          </Button>
                        );
                      })}
                      <p>Prices shown before existing eligible discounts and applicable tax.</p>
                      <Button
                        onClick={changePlan}
                        disabled={!selected || updating}
                        variant="contained"
                      >
                        {updating ? 'Saving…' : 'Confirm plan change'}
                      </Button>
                    </Box>
                  )}
                  {pricing.error && (
                    <p role="alert">
                      {pricing.error} <Button onClick={pricing.reload}>Retry prices</Button>
                    </p>
                  )}
                </>
              )}
              {details.outstandingInvoice && (
                <Box sx={{ border: '1px solid #CA7C62', p: 2, mt: 2 }}>
                  <h3>Outstanding invoice</h3>
                  <p>
                    {formatPrice(
                      details.outstandingInvoice.amountDue,
                      details.outstandingInvoice.currency,
                    )}{' '}
                    is due for your existing subscription, including its applicable tax and
                    discounts. New plan prices do not change this invoice.
                  </p>
                  {details.outstandingInvoice.hostedInvoiceUrl?.startsWith(
                    'https://invoice.stripe.com/',
                  ) && (
                    <Button
                      component="a"
                      href={details.outstandingInvoice.hostedInvoiceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Review and pay invoice in Stripe
                    </Button>
                  )}
                  <Button
                    onClick={() => {
                      dispatch(getSubscriptionStatus());
                      setRevision((value) => value + 1);
                    }}
                  >
                    Refresh payment status
                  </Button>
                </Box>
              )}
              <h2>Payment details</h2>
              {!showPayment && (
                <Button onClick={() => setShowPayment(true)} variant="outlined">
                  {details.hasPaymentMethodId ? 'Update payment details' : 'Add payment details'}
                </Button>
              )}
              {showPayment &&
                (stripePromise ? (
                  <Elements stripe={stripePromise}>
                    <PaymentMethodForm
                      onSaved={() => {
                        setShowPayment(false);
                        dispatch(getSubscriptionStatus());
                        setRevision((value) => value + 1);
                        setMessage(
                          'Payment details updated. Your plan and discounts are unchanged.',
                        );
                      }}
                    />
                  </Elements>
                ) : (
                  <p role="alert">
                    Card updates are temporarily unavailable. Please try again later.
                  </p>
                ))}
              <p>
                For help with your subscription or to cancel, email{' '}
                <a href="mailto:support@museflow.ai">support@museflow.ai</a>.
              </p>
            </>
          )}
          {error && (
            <p role="alert">
              {error}{' '}
              {!details && <Button onClick={() => setRevision((value) => value + 1)}>Retry</Button>}
            </p>
          )}
          {message && <p role="status">{message}</p>}
        </>
      )}
    </Box>
  );
};
export default Billing;
